import { useContext, useReducer, createContext, useState, useCallback } from 'react';
import isEqual from 'react-fast-compare';
import { produce } from 'immer';

import { getParam, getInitialValue } from '/src/utils/location';
import { useQueryParams } from '/src/hooks';
import { FUNNELS_ORDER } from './constants';
import { SORT_ORDER_TYPES } from '/src/features/_global/constants';

const FILTER_ACTIONS = {
  SET: 'SET',
  RESET: 'RESET',
  SET_ORDER_FIELD: 'SET_ORDER_FIELD',
  SET_ORDER_SORT: 'SET_ORDER_SORT',
};
const { SET, RESET, SET_ORDER_FIELD, SET_ORDER_SORT } = FILTER_ACTIONS;

const FilterContext = createContext();

const initialState = () => {
  return {
    search: getParam('search') || '',
    order: {
      field: getInitialValue('order', [...Object.values(FUNNELS_ORDER)]) || FUNNELS_ORDER.CREATED_AT,
      sort: getInitialValue('sort', [SORT_ORDER_TYPES.ASC, SORT_ORDER_TYPES.DESC]) || SORT_ORDER_TYPES.DESC,
    },
  };
};

export function FunnelsFilterProvider({ children }) {
  function filterReducer(state = initialState(), action) {
    switch (action.type) {
      case SET:
        return {
          ...state,
          ...action.payload,
        };
      case SET_ORDER_FIELD:
        return produce(state, (draft) => {
          draft.order.field = action.payload;
        });
      case SET_ORDER_SORT:
        return produce(state, (draft) => {
          draft.order.sort = action.payload;
        });
      case RESET:
        return {
          ...initialState(),
        };
      default:
        return state;
    }
  }

  const [filter, filterDispatch] = useReducer(filterReducer, initialState());
  const [filterState, setFilterState] = useState({ filter, filterDispatch });
  if (!isEqual(filter, filterState.filter)) {
    setFilterState({ filter, filterDispatch });
  }
  return <FilterContext.Provider value={filterState}>{children}</FilterContext.Provider>;
}

export function useFilter() {
  const { set: queryParamsSet, removeAll } = useQueryParams();
  const { filter, filterDispatch } = useContext(FilterContext);

  const setSearch = useCallback(
    (search) => {
      queryParamsSet('search', search);
      filterDispatch({
        type: SET,
        payload: {
          search,
        },
      });
    },
    [queryParamsSet, filterDispatch],
  );

  const setSort = useCallback(
    (sort) => {
      queryParamsSet('sort', sort);
      filterDispatch({ type: SET_ORDER_SORT, payload: sort });
    },
    [filterDispatch, queryParamsSet],
  );

  const setOrder = useCallback(
    (order) => {
      queryParamsSet('order', order);
      filterDispatch({ type: SET_ORDER_FIELD, payload: order });
    },
    [filterDispatch, queryParamsSet],
  );

  function reset() {
    removeAll(['search']);
    filterDispatch({
      type: RESET,
    });
    window.dispatchEvent(new Event('filters:reset'));
  }

  return {
    ...filter,
    setSearch,
    setSort,
    setOrder,
    reset,
  };
}
